/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import Datadog from '@ion/core/src/analytics/Datadog';
import Dynatrace from '@ion/core/src/analytics/Dynatrace';
import getMonitor from '@ion/telemetry/src/apm/monitors/CrashEventMonitor';

export interface UseErrorHandlerResults {
  error: boolean;
  onRefreshHandler: Function;
  onErrorHandler: (e: Error) => void;
};

/**
 * useErrorHandler is a custom React hook that provides error handling functionality
 * for components. It's designed to manage errors, trigger a refresh function, and
 * allow custom error handling. This hook is especially useful for components that
 * need to handle errors gracefully, show an error message, and offer the option to
 * refresh or recover from the error.
 *
 * ```typescript
 *
 *  import {useErrorHandler} from '@ion/dls/src/components/errorHandler';
 *
 *  const {error, onRefreshHandler, onErrorHandler} = useErrorHandler(onRefresh, onError);
 *
 * ```
 *
 * @param onRefresh A function that typically used to recover from an error
 * @param onError A custom error handler function that can be defined to handle specific error scenarios
 */
export const useErrorHandler = (
  onRefresh?: Function,
  onError?: Function
): UseErrorHandlerResults => {
  const [error, setError] = useState(null);

  const onErrorHandler = useCallback((err) => {
    getMonitor()?.onCrash(err);
    if (Dynatrace.isEnabled()) {
      Dynatrace.reportError(err);
    }
    if (Datadog.isEnabled()) {
      Datadog.reportPageCrash(err);
    }
    onError?.(err);
    setError(!!err);
  }, [onError]);

  // Handle the refresh request.
  const onRefreshHandler = useCallback(() => {
    onRefresh?.();
    setError(null);
  }, [onRefresh]);

  useEffect(() => {
    return () => {
      if (error) {
        onRefreshHandler();
      }
    };
  }, [error]);

  return {
    onRefreshHandler,
    onErrorHandler,
    error
  };
};

export default useErrorHandler;
